import React, { useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import TopNavigation from './TopNavigation'
import Footer from './Footer'

const TermsOfSale = () => {
  const [isSecretRoute, setIsSecretRoute] = useState(false)
  const location = useLocation()

  useEffect(() => {
    const hostname = window.location.hostname
    const pathname = window.location.pathname
    const isSecretSubdomain = hostname.startsWith('secret.') || hostname === 'secret.localhost'
    const isSecretPath = pathname.startsWith('/secret')
    setIsSecretRoute(isSecretSubdomain || isSecretPath)
  }, [location.pathname])

  return (
    <div className="min-h-screen bg-background">
      <TopNavigation backTo="/" backText="Back to Home" />

      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="prose prose-lg max-w-none">
          <h1 className="text-4xl font-bold text-foreground mb-8">Terms of Sale</h1>
          <p className="text-muted-foreground mb-8">
            <strong>Last updated:</strong> July, 2026
          </p>
          <p className="text-muted-foreground mb-8">
            <strong>Tailtations LLC</strong> - A Wyoming Limited Liability Company
          </p>
          <p className="text-muted-foreground leading-relaxed mb-8">
            Welcome to Tailtations® ("Company," "we," "our," or "us"). By placing an order through our SECRET platform, our nationwide pre-order store (tailtations.com/taste), or attending a Tailtations popup event, you ("Customer," "you," or "your") agree to the following Terms of Sale. Please read carefully. These terms constitute a legally binding agreement between you and Tailtations LLC and govern all orders placed through any Tailtations sales channel.
          </p>

          <div className="space-y-8">
            {/* ============================================================ */}
            {/* SECTION A: NATIONWIDE PRE-ORDER (TASTE OF TAILTATIONS)       */}
            {/* ============================================================ */}
            <section className="p-6 bg-muted/50 rounded-lg border border-border">
              <h2 className="text-2xl font-semibold text-foreground mb-2">SECTION A: NATIONWIDE PRE-ORDER TERMS</h2>
              <p className="text-sm text-muted-foreground mb-6 italic">
                Applicable to orders placed through tailtations.com/taste ("Taste of Tailtations")
              </p>

              <h3 className="text-xl font-medium text-foreground mb-3">A.1 Pre-Order Acknowledgment</h3>
              <p className="text-muted-foreground leading-relaxed mb-4">
                By placing an order on the Taste of Tailtations store, you acknowledge and agree that you are purchasing a <strong>"Pre-Order"</strong> item. This means the product is currently in production and is not available for immediate dispatch. Your order will be fulfilled as part of a scheduled batch shipment.
              </p>

              <h3 className="text-xl font-medium text-foreground mb-3 mt-6">A.2 Batch Shipping Schedule</h3>
              <p className="text-muted-foreground leading-relaxed mb-4">
                We operate on a strict batch-shipping model to ensure maximum quality and freshness. All orders placed during the current promotional period <strong>(July 2 – September 1, 2026)</strong> will be processed and shipped via UPS 2-Day Air no later than <strong>September 2, 2026</strong>, with expected delivery by September 4, 2026 — in time for Labor Day weekend. By completing your purchase, you explicitly consent to this shipping timeline. We will notify you via email immediately if there are any unforeseen delays to the batch schedule.
              </p>

              <h3 className="text-xl font-medium text-foreground mb-3 mt-6">A.3 Payment Processing</h3>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Payments are processed immediately upon checkout via Square to secure your allocation in our limited batch run. <strong>Your payment method will be charged in full at the time of order, not at the time of shipment.</strong>
              </p>

              <h3 className="text-xl font-medium text-foreground mb-3 mt-6">A.4 Cancellations and Refunds (Pre-Orders)</h3>
              <p className="text-muted-foreground leading-relaxed mb-2">
                Because our production is strictly tied to the exact number of pre-orders received:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2 mb-4">
                <li><strong>Within 48 Hours of Purchase:</strong> You may cancel your order for a full refund by contacting our support team at <a href="mailto:contact@tailtations.com" className="text-primary hover:underline">contact@tailtations.com</a>.</li>
                <li><strong>After 48 Hours:</strong> We cannot accept cancellations or offer refunds, as your order has already been factored into our physical batch production.</li>
                <li><strong>Fulfillment Failure:</strong> In the unlikely event that we cannot fulfill your order by the stated batch shipping date (or a mutually agreed-upon revised date), you will promptly receive a full refund to your original payment method.</li>
              </ul>

              <h3 className="text-xl font-medium text-foreground mb-3 mt-6">A.5 Perishable Goods Disclaimer</h3>
              <p className="text-muted-foreground leading-relaxed mb-4">
                All Tailtations products shipped via our nationwide pre-order program are <strong>perishable food items</strong>. Products are flash-frozen and shipped in insulated packaging with dry ice. Upon receipt, you must:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2 mb-4">
                <li>Inspect your package immediately upon delivery.</li>
                <li>Transfer all items to a freezer (0°F / -18°C or below) within 30 minutes of receiving your shipment.</li>
                <li>Follow all reheating instructions included in your package.</li>
                <li>Consume within 90 days of the ship date for optimal quality.</li>
              </ul>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Tailtations is not liable for product spoilage resulting from delayed package retrieval, improper storage, or failure to follow handling instructions.
              </p>

              <h3 className="text-xl font-medium text-foreground mb-3 mt-6">A.6 Allergen Notice</h3>
              <p className="text-muted-foreground leading-relaxed mb-4">
                <strong>Our products contain shellfish (lobster) and may contain or come into contact with: wheat, eggs, milk, soy, and tree nuts.</strong> Our kitchen processes multiple allergens. If you have a food allergy or sensitivity, please contact us before ordering at <a href="mailto:contact@tailtations.com" className="text-primary hover:underline">contact@tailtations.com</a>. Tailtations is not responsible for allergic reactions resulting from undisclosed allergies or sensitivities.
              </p>

              <h3 className="text-xl font-medium text-foreground mb-3 mt-6">A.7 Shipping Restrictions</h3>
              <p className="text-muted-foreground leading-relaxed mb-4">
                We currently ship to all 50 U.S. states and Washington, D.C. We do not ship internationally or to P.O. Boxes. A valid street address is required for all shipments. Someone must be available to receive the package on the delivery date, as perishable shipments cannot be left unattended for extended periods.
              </p>
            </section>

            {/* ============================================================ */}
            {/* SECTION B: SECRET PLATFORM & EVENT ORDERS                    */}
            {/* ============================================================ */}
            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-2">SECTION B: SECRET PLATFORM & EVENT ORDER TERMS</h2>
              <p className="text-sm text-muted-foreground mb-6 italic">
                Applicable to orders placed through the SECRET platform and at Tailtations popup events
              </p>

              <h3 className="text-xl font-medium text-foreground mb-3">B.1 All Sales Are Final</h3>
              <p className="text-muted-foreground leading-relaxed mb-4">
                By completing your purchase through the SECRET platform, you acknowledge and agree that all sales are final. Due to the perishable nature of our gourmet food products and the exclusive, pre-order-based model of our events, refunds, cancellations, or modifications are not permitted once an order has been placed.
              </p>

              <h3 className="text-xl font-medium text-foreground mb-3 mt-6">B.2 Event Cancellation by Tailtations</h3>
              <p className="text-muted-foreground leading-relaxed mb-4">
                In the rare event that Tailtations cancels a popup event due to unforeseen circumstances (e.g., severe weather, regulatory restrictions, or event site issues), you will be provided with a credit equal to your order amount. This credit can be applied to future events through the SECRET platform. Refunds in cash or to your original payment method will not be issued.
              </p>

              <h3 className="text-xl font-medium text-foreground mb-3 mt-6">B.3 Customer No-Shows or Late Arrivals</h3>
              <p className="text-muted-foreground leading-relaxed mb-4">
                It is your responsibility to arrive at the designated pickup location during the assigned time window specified in your order confirmation. Orders not claimed within the stated pickup window will not be refunded, rescheduled, or credited, as per Wyoming's and California's consumer laws regarding services rendered under specific time constraints.
              </p>

              <h3 className="text-xl font-medium text-foreground mb-3 mt-6">B.4 Order Errors or Discrepancies</h3>
              <p className="text-muted-foreground leading-relaxed mb-4">
                In the event of an error with your order (e.g., incorrect items provided), you must notify Tailtations staff at the time of pickup. We will make reasonable efforts to correct the issue on-site. Failure to report discrepancies at the time of pickup forfeits your right to dispute the order.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">C. WYOMING AND CALIFORNIA STATE LAWS</h2>

              <h3 className="text-xl font-medium text-foreground mb-3">C.1 Compliance with Wyoming Law</h3>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Under Wyoming's Uniform Consumer Credit Code (Wyo. Stat. § 40-14-101 et seq.), prepaid services such as those offered by Tailtations are governed by contract. By agreeing to these terms, you acknowledge that your pre-payment for gourmet food constitutes a binding service agreement, which Tailtations fulfills upon your order being prepared and available during the scheduled event.
              </p>

              <h3 className="text-xl font-medium text-foreground mb-3 mt-6">C.2 Compliance with California Law</h3>
              <p className="text-muted-foreground leading-relaxed mb-4">
                In accordance with California's Consumers Legal Remedies Act (CLRA, Cal. Civ. Code § 1750 et seq.) and other applicable statutes, Tailtations ensures transparency in all transactions. By pre-ordering, you waive the right to request refunds under California's "cooling-off" rule, as this rule does not apply to perishable goods or time-specific services. Tailtations provides clear event details, including location, time, and menu availability, prior to purchase, ensuring informed consent.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">D. ORDERING PROCESS</h2>

              <h3 className="text-xl font-medium text-foreground mb-3">D.1 Pre-Order Requirements</h3>
              <p className="text-muted-foreground leading-relaxed mb-4">
                All orders must be placed through the SECRET platform. Orders are subject to availability and are not confirmed until payment is successfully processed and you receive an email/SMS confirmation.
              </p>

              <h3 className="text-xl font-medium text-foreground mb-3 mt-6">D.2 Payment Authorization</h3>
              <p className="text-muted-foreground leading-relaxed mb-4">
                By providing payment information during checkout, you authorize Tailtations and its payment processor (Square) to charge your chosen method of payment for the full order amount, including any applicable taxes and fees.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">E. LIMITATION OF LIABILITY</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Tailtations LLC is not liable for:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Delays or disruptions caused by factors outside our control, including but not limited to weather, traffic, or local regulations.</li>
                <li>Allergic reactions or food sensitivities not disclosed to us prior to placing an order.</li>
              </ul>
              <p className="text-muted-foreground leading-relaxed mt-4">
                To the fullest extent permitted by law, Tailtations LLC's total liability for any claims related to your order is limited to the amount paid for the specific order in question.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">F. DISPUTE RESOLUTION</h2>

              <h3 className="text-xl font-medium text-foreground mb-3">F.1 Governing Law</h3>
              <p className="text-muted-foreground leading-relaxed mb-4">
                This Agreement is governed by the laws of the State of Wyoming. However, if you reside in California, you may have additional consumer rights under California law.
              </p>

              <h3 className="text-xl font-medium text-foreground mb-3 mt-6">F.2 Arbitration Clause</h3>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Any disputes arising from or relating to these Terms of Sale, including disputes regarding interpretation, breach, or enforcement, shall be resolved exclusively through binding arbitration in accordance with the rules of the American Arbitration Association ("AAA"). Arbitration will take place in Los Angeles, California, and each party will bear its own legal fees and costs.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">G. CHANGES TO TERMS OF SALE</h2>
              <p className="text-muted-foreground leading-relaxed">
                Tailtations reserves the right to update or modify these Terms of Sale at any time. Updated terms will be posted on the SECRET platform and will apply to all orders placed after the effective date of the update.
              </p>
            </section>

            <section className="mt-8 p-6 bg-muted rounded-lg border-2 border-foreground/20">
              <p className="text-foreground font-semibold text-center leading-relaxed">
                BY PLACING AN ORDER THROUGH ANY TAILTATIONS SALES CHANNEL, YOU CONFIRM THAT YOU HAVE READ, UNDERSTOOD, AND AGREED TO THESE TERMS OF SALE. IF YOU DO NOT AGREE, DO NOT PLACE AN ORDER.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">Contact Information</h2>
              <p className="text-muted-foreground leading-relaxed">
                If you have any questions about these Terms of Sale, please contact us at:
              </p>
              <div className="mt-4 p-4 bg-muted rounded-lg">
                <p className="text-foreground font-medium">Tailtations LLC</p>
                <p className="text-muted-foreground">Email: legal@tailtations.com</p>
              </div>
            </section>
          </div>
        </div>
      </div>
      {!isSecretRoute && <Footer />}
    </div>
  )
}

export default TermsOfSale
